import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { getEnumTKey } from "@/utils/strings";
import {
  PersonData,
  PersonReferenceabilityLevel,
  PersonStatus
} from "@/entities/people/types";
import {
  referenceabilityLevels,
  referenceabilityLevelsToScoreBarsMap
} from "@/entities/people/helpers";
import ScoreBars, { ScoreStrength } from "@/components/controls/ScoreBars";
import TopFields, { FieldsConfig } from "@/components/layouts/TopFields";
import { personStatusColors } from "@/components/layouts/People/PeopleTable";
import Text from "@/components/ui/text";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";
import { useDeals } from "@/pages/deals/hooks";
import { useLeads } from "@/pages/leads/hooks";
import {
  EntityType,
  useEntityType
} from "@/services/hooks/entities/useEntityType";
import { useUsers } from "@/entities/users/hooks";
import DetailsPopover from "@/components/controls/DetailsPopover";
import { useEntitySelect } from "@/services/hooks/entities/useEntitySelect";
import IconContainer from "@/components/ui/icon-container";
import { Bullet } from "@dreamhub/designsystem/icons";
import { map } from "lodash";

interface Props {
  person?: PersonData;
  onUpdateField: (field: string, newValue: string | number) => void;
  openEditQualificationModal?: () => void;
  className?: string;
  itemClassName?: string;
}

const PersonTopFields: React.FC<Props> = ({
  person,
  onUpdateField,
  openEditQualificationModal,
  className,
  itemClassName
}) => {
  const { t } = useTranslation();

  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  const relatedEntityType =
    person?.status === PersonStatus.ENGAGED_IN_DEAL
      ? EntityType.DEAL
      : person?.status === PersonStatus.ENGAGED_IN_LEAD
        ? EntityType.LEAD
        : undefined;

  const {
    entities: [dealEntity]
  } = useDeals([person?.activeDealId || ""], {
    enabled: relatedEntityType === EntityType.DEAL
  });

  const {
    entities: [leadEntity]
  } = useLeads([person?.activeLeadId || ""], {
    enabled: relatedEntityType === EntityType.LEAD
  });

  const changedByUser = useUsers([person?.statusChangedBy || ""]).users[0];

  const relatedEntity =
    relatedEntityType === EntityType.DEAL
      ? dealEntity
      : relatedEntityType === EntityType.LEAD
        ? leadEntity
        : undefined;

  const { openDetailedView } = useEntityType(
    relatedEntityType ?? EntityType.DEAL,
    relatedEntity
  );

  const {
    options: companyOptions,
    onSearch: onCompanyOptionsSearch,
    isLoading: isLoadingCompanies,
    searchTerm: companySearchTerm
  } = useEntitySelect({
    entityType: EntityType.COMPANY,
    selectedId: person?.companyId || "",
    alwaysEnabled: true
  });

  const renderStatus = () => (
    <div className="flex items-center gap-1 pr-2">
      <IconContainer
        style={{ color: personStatusColors[person?.status as PersonStatus] }}
      >
        <Bullet />
      </IconContainer>
      <Text variant="body">
        {t(
          getEnumTKey(
            "people",
            "statuses",
            PersonStatus,
            person?.status as number
          )
        )}
      </Text>
    </div>
  );

  const renderDisqualifiedStatus = () => (
    <div className="flex gap-[2px] items-center pr-2">
      {renderStatus()}
      <DetailsPopover
        isOpen={isPopoverOpen}
        setIsOpen={setIsPopoverOpen}
        onEdit={openEditQualificationModal}
        statusReasonText={person?.statusReasonText}
        statusChangedAt={person?.statusChangedAt}
        changedByUser={changedByUser}
      />
    </div>
  );

  const fieldsConfig: FieldsConfig = {
    companyId: {
      type: "select",
      icon: "company",
      entity: EntityType.COMPANY,
      options: companyOptions,
      searchTerm: companySearchTerm,
      showSearch: true,
      onSearch: onCompanyOptionsSearch,
      isLoading: isLoadingCompanies,
      popoverClassName: "max-w-[30vw]"
    },
    jobTitle: {
      type: "editableInput",
      icon: "job",
      initialValue: person?.jobTitle || ""
    },
    status: {
      type: "nonEditable",
      customComponent:
        person?.status === PersonStatus.DISQUALIFIED ? (
          renderDisqualifiedStatus()
        ) : (
          <Tooltip>
            <TooltipTrigger>{renderStatus()}</TooltipTrigger>
            <TooltipContent sideOffset={4}>
              <div className="flex gap-1">
                {t(
                  getEnumTKey(
                    "people",
                    "statusesTooltips",
                    PersonStatus,
                    person?.status as number
                  )
                )}
                {relatedEntity && (
                  <Text
                    variant="smLink"
                    className="cursor-pointer"
                    onClick={
                      relatedEntityType ? () => openDetailedView() : undefined
                    }
                  >
                    {relatedEntity?.name}
                  </Text>
                )}
              </div>
            </TooltipContent>
          </Tooltip>
        )
    },
    referenceability: {
      type: "select",
      defaultValue: PersonReferenceabilityLevel.UNKNOWN,
      options: map(referenceabilityLevels, (level) => ({
        id: level,
        name: PersonReferenceabilityLevel[level]
      })),
      formatter: (value) =>
        t(
          getEnumTKey(
            "people",
            "refLevels",
            PersonReferenceabilityLevel,
            value as number
          )
        ),
      renderOptionIcon: (value) => (
        <ScoreBars
          score={
            referenceabilityLevelsToScoreBarsMap[Number(value)] as ScoreStrength
          }
        />
      ),
      additionalProps: {
        className: "whitespace-normal break-words",
        popoverClassName: "!max-w-fit"
      }
    }
  };

  return (
    <TopFields
      entity={person}
      fieldsConfig={fieldsConfig}
      onUpdateField={onUpdateField}
      className={className}
      itemClassName={itemClassName}
    />
  );
};

export default PersonTopFields;
